/**
 * AdminCompliance — Tenant-Admin self-service view.
 *  • Shows status (AVV, DSGVO, TOMs signed?) for own tenant
 *  • Lists signed docs with download button (→ /api/admin/compliance/signed-docs/{id}/pdf)
 *  • Shows banner for legacy tenants (not required to sign)
 */
import { useEffect, useState } from "react";
import { ShieldCheck, FileText, Download, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AdminCompliance() {
  const [status, setStatus] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/compliance/status"),
      api.get("/admin/compliance/signed-docs"),
    ])
      .then(([s, d]) => {
        setStatus(s.data);
        setDocs(d.data?.docs || []);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Fehler beim Laden"))
      .finally(() => setLoading(false));
  }, []);

  const downloadPdf = async (id, slug) => {
    try {
      const r = await api.get(`/admin/compliance/signed-docs/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}-${id.slice(0, 8)}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Download fehlgeschlagen");
    }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto text-zinc-500" /></div>;

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-4" data-testid="admin-compliance-page">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="text-[#00E575]" size={22} />
        <h1 className="font-display text-2xl font-bold">Compliance & Verträge</h1>
      </div>
      <p className="text-zinc-300 text-sm font-medium">
        Hier finden Sie Ihre digital unterzeichneten Datenschutz- und Auftragsverarbeitungs-Verträge.
        Jedes Dokument ist mit SHA-256-Hash, Zeitstempel und IP-Audit revisionssicher gespeichert.
      </p>

      {status?.is_legacy_tenant && (
        <div className="bg-amber-900/20 border border-amber-700/40 rounded-xl p-4" data-testid="compliance-legacy-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle className="text-amber-400 flex-shrink-0" size={18} />
            <div>
              <div className="font-bold text-amber-300 mb-1">Bestandsmandant</div>
              <p className="text-[12.5px] text-amber-100/80">
                Ihr Mandant wurde vor Einführung des digitalen AVV-Workflows angelegt.
                Sie sind <b>nicht verpflichtet</b>, nachträglich elektronisch zu signieren.
                Falls Sie das dennoch wünschen, sprechen Sie bitte Ihren Account-Manager an.
              </p>
            </div>
          </div>
        </div>
      )}

      {!status?.is_legacy_tenant && (
        <div className="grid grid-cols-3 gap-2" data-testid="compliance-status-grid">
          <StatusCard label="AVV (Art. 28)" ok={status?.avv_signed} />
          <StatusCard label="DSGVO-Verpflichtung" ok={status?.dsgvo_signed} />
          <StatusCard label="TOMs (Anlage 1)" ok={status?.toms_signed} />
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold">Signierte Dokumente</h2>
          {docs.length > 0 && (
            <button
              onClick={async () => {
                try {
                  const r = await api.get("/admin/compliance/audit-trail-pdf", { responseType: "blob" });
                  const url = URL.createObjectURL(r.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `audit-trail-${Date.now()}.pdf`;
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                } catch (e) {
                  toast.error("Audit-Trail-Download fehlgeschlagen");
                }
              }}
              data-testid="compliance-audit-trail-pdf"
              className="px-3 h-8 rounded-lg bg-amber-500 text-[#09090B] text-[12px] font-bold inline-flex items-center gap-1.5 hover:bg-amber-400"
            >
              <Download size={12} /> Audit-Trail (alle in 1 PDF)
            </button>
          )}
        </div>
        {docs.length === 0 ? (
          <div className="text-center text-zinc-500 p-6 bg-[#18181B] border border-zinc-800 rounded-xl">
            Keine signierten Dokumente vorhanden.
          </div>
        ) : (
          docs.map((d) => (
            <div key={d.id} data-testid={`compliance-doc-${d.slug}`}
                 className="flex items-center gap-3 bg-[#18181B] border border-zinc-800 rounded-xl p-3 hover:border-zinc-700">
              <FileText className="text-amber-400 flex-shrink-0" size={20} />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-bold text-zinc-100">{d.title}</div>
                <div className="text-[12px] text-zinc-300">
                  v{d.doc_version} · unterzeichnet am {new Date(d.signed_at).toLocaleString("de-DE")} ·
                  von <b>{d.signed_name}</b>
                </div>
                <div className="text-[9px] text-zinc-400 font-mono mt-0.5">
                  sha256:{d.doc_hash?.slice(0, 24)}…
                </div>
              </div>
              <button onClick={() => downloadPdf(d.id, d.slug)}
                      data-testid={`compliance-doc-pdf-${d.slug}`}
                      className="px-3 h-9 rounded-lg bg-[#00E575] text-[#09090B] text-[12px] font-bold inline-flex items-center gap-1.5 hover:bg-[#00C964]">
                <Download size={12} /> PDF
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const StatusCard = ({ label, ok }) => (
  <div className={`p-3 rounded-xl border flex items-center gap-2 ${ok ? "bg-emerald-900/20 border-emerald-700/40" : "bg-zinc-900 border-zinc-800"}`}>
    {ok
      ? <CheckCircle2 className="text-[#00E575] flex-shrink-0" size={16} />
      : <AlertTriangle className="text-zinc-500 flex-shrink-0" size={16} />}
    <div>
      <div className="text-[12px] uppercase tracking-wider text-zinc-400 font-bold">{label}</div>
      <div className={`text-[12px] ${ok ? "text-emerald-300" : "text-zinc-300"}`}>
        {ok ? "signiert" : "offen"}
      </div>
    </div>
  </div>
);
